import axios from 'axios';
import { useAppStore } from '@/stores/appStore';

const API_BASE_URL = import.meta.env.VITE_API_URL || '/api';

export const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor - add auth token and tahun buku
api.interceptors.request.use(
  (config) => {
    // Add auth token
    const token = localStorage.getItem('accessToken');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    // Add X-Tahun-Buku header untuk semua request
    // Backend menggunakan header ini untuk query database transaksi per tahun buku.
    //
    // PENTING — interceptor ini menyediakan NILAI DEFAULT, bukan override paksa.
    // Kalau pemanggil sudah men-set header ini secara eksplisit per-request
    // (mis. `gajiService.getAllForYear` saat user filter Tahun ke tahun selain
    // TahunBukuAktif), nilai pemanggil HARUS menang. Sebelum 09-08-2026
    // interceptor menimpanya tanpa syarat sehingga fitur "ganti Tahun = ganti
    // konteks DB" tidak pernah bekerja — filter berubah, data tetap sama,
    // tanpa error apa pun.
    const { TahunBukuAktif, JenisTahunBuku } = useAppStore.getState();
    if (TahunBukuAktif && !config.headers.has('X-Tahun-Buku')) {
      config.headers['X-Tahun-Buku'] = TahunBukuAktif.toString();
    }
    if (JenisTahunBuku && !config.headers.has('X-Jenis-Tahun-Buku')) {
      config.headers['X-Jenis-Tahun-Buku'] = JenisTahunBuku;
    }

    return config;
  },
  (error) => Promise.reject(error)
);

// Response interceptor - handle token refresh
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    // If 401 and not already retrying
    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;

      try {
        const refreshToken = localStorage.getItem('refreshToken');
        if (refreshToken) {
          const response = await axios.post(`${API_BASE_URL}/auth/refresh`, {
            refreshToken,
          });

          const { accessToken, refreshToken: newRefreshToken } = response.data;
          localStorage.setItem('accessToken', accessToken);
          localStorage.setItem('refreshToken', newRefreshToken);

          originalRequest.headers.Authorization = `Bearer ${accessToken}`;
          return api(originalRequest);
        }
      } catch {
        // Refresh failed, clear tokens
        localStorage.removeItem('accessToken');
        localStorage.removeItem('refreshToken');
        window.location.href = '/login';
      }
    }

    return Promise.reject(error);
  }
);

export default api;
